import { useCallback, useEffect, useState } from "react";
import { History, Landmark, Search, Wallet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FibiLoading } from "../components/FibiLoading";
import { DatePicker } from "../components/DatePicker";

const STATUS_LABEL = { approved: "Sukses", cancelled: "Cancel", rejected: "Ditolak", expired: "Expired", pending: "Pending" };
const STATUS_BADGE = {
  approved: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  cancelled: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  rejected: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  expired: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  pending: "bg-sky-500/15 text-sky-500 border-sky-500/30",
};
const TABS = [["", "Semua"], ["approved", "Sukses"], ["cancel", "Cancel"], ["expired", "Expired"]];
const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const fmtDT = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function DepositHistory() {
  const { user: me } = useAuth();
  const isSuper = me?.role === "admin";
  const [approving, setApproving] = useState(null);
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const approveExpired = async (d) => {
    const total = Number(d.amount || 0) + Number(d.unique_code || 0);
    if (!window.confirm(`Setujui deposit EXPIRED milik ${d.name || d.email}?\nSaldo agen akan bertambah ${rupiah(total)} (pastikan transfer sudah masuk ke rekening).`)) return;
    setApproving(d.id);
    try {
      await api.post(`/balance/topup-requests/${d.id}/approve`);
      toast.success(`Deposit ${d.name || d.email} disetujui, saldo +${rupiah(total)}`);
      load(status, dateFrom, dateTo);
    } catch (e) { toast.error(errMsg(e)); } finally { setApproving(null); }
  };

  const load = useCallback((st, df, dt) => {
    setItems(null);
    const params = {};
    if (st) params.status = st;
    if (df) params.date_from = df;
    if (dt) params.date_to = dt;
    api.get("/balance/topup-requests", { params })
      .then((r) => setItems(r.data))
      .catch((e) => { setItems([]); toast.error(errMsg(e)); });
  }, []);
  useEffect(() => { load(status, dateFrom, dateTo); }, [status, dateFrom, dateTo, load]);

  const filtered = (items || []).filter((d) =>
    !q || (d.name || "").toLowerCase().includes(q.toLowerCase()) || (d.email || "").toLowerCase().includes(q.toLowerCase()));

  const totalSukses = (items || []).filter((d) => d.status === "approved").reduce((s, d) => s + (d.amount || 0), 0);
  const bankStats = Object.values((items || []).filter((d) => d.status === "approved").reduce((acc, d) => {
    const b = d.bank || "Lainnya";
    const a = acc[b] || (acc[b] = { bank: b, total: 0, count: 0, agents: {} });
    a.total += d.amount || 0; a.count += 1;
    const k = d.email || d.name || "-";
    const ag = a.agents[k] || (a.agents[k] = { name: d.name || d.email, total: 0, count: 0 });
    ag.total += d.amount || 0; ag.count += 1;
    return acc;
  }, {})).map((a) => ({ ...a, top: Object.values(a.agents).sort((x, y) => y.total - x.total)[0] })).sort((x, y) => y.total - x.total);

  return (
    <div className="space-y-4" data-testid="deposit-history-page">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
          <History className="w-6 h-6 text-emerald-500" /> History Deposit Auto
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Riwayat semua request deposit agen — sukses, cancel, dan expired (request kedaluwarsa otomatis setelah 10 menit).</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Wallet className="w-4 h-4 text-emerald-500" /> Riwayat Deposit
            {(status === "approved" || dateFrom || dateTo) && <span className="text-xs font-normal text-emerald-500" data-testid="deposit-total-sukses">Total sukses: {rupiah(totalSukses)}</span>}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <DatePicker className="h-9 w-[145px]" value={dateFrom} onChange={(v) => setDateFrom(v)} testid="input-date-from" placeholder="Dari tanggal" />
            <span className="text-xs text-muted-foreground">s/d</span>
            <DatePicker className="h-9 w-[145px]" value={dateTo} onChange={(v) => setDateTo(v)} testid="input-date-to" placeholder="Sampai tanggal" />
            {(dateFrom || dateTo) && <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="btn-clear-date">Reset</Button>}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 h-9" placeholder="Cari nama / email agen..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="input-search-deposit" />
          </div>
        </CardHeader>
        <div className="px-4 sm:px-6 pb-3 flex items-center gap-1.5 flex-wrap" data-testid="deposit-status-tabs">
          {TABS.map(([v, label]) => (
            <button key={v || "all"} type="button" onClick={() => setStatus(v)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${status === v ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "bg-transparent text-muted-foreground border-border hover:bg-accent"}`}
              data-testid={`tab-deposit-${v || "all"}`}>{label}</button>
          ))}
        </div>
        {bankStats.length > 0 && (
          <div className="px-4 sm:px-6 pb-4" data-testid="deposit-bank-stats">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Deposit sukses per bank{dateFrom || dateTo ? " (periode terpilih)" : ""} • total {rupiah(totalSukses)}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
              {bankStats.map((b) => (
                <div key={b.bank} className="rounded-lg border border-border bg-background/50 px-3 py-2" data-testid={`bank-stat-${b.bank}`}>
                  <div className="flex items-baseline justify-between gap-2"><span className="font-semibold text-sm">{b.bank}</span><span className="text-[10px] font-mono text-muted-foreground">{b.count} trx • {totalSukses ? Math.round((b.total / totalSukses) * 100) : 0}%</span></div>
                  <div className="font-mono font-bold text-emerald-400 text-sm" data-testid={`bank-total-${b.bank}`}>{rupiah(b.total)}</div>
                  <div className="h-1 rounded-full bg-accent overflow-hidden my-1"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${totalSukses ? (b.total / totalSukses) * 100 : 0}%` }} /></div>
                  {b.top && <div className="text-[10px] text-muted-foreground truncate" title={`${b.top.name} • ${b.top.count} trx`} data-testid={`bank-top-${b.bank}`}>Terbanyak: <span className="text-foreground font-medium">{b.top.name}</span> ({rupiah(b.top.total)}, {b.top.count}x)</div>}
                </div>
              ))}
            </div>
          </div>
        )}
        <CardContent className="p-0 overflow-x-auto">
          {items === null ? (
            <div data-testid="deposit-loading"><FibiLoading text="FIBI sedang memuat riwayat..." size={20} /></div>
          ) : (
            <table className="w-full text-sm min-w-[860px]" data-testid="deposit-table">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5">Waktu</th>
                  <th className="px-4 py-2.5">Agen</th>
                  <th className="px-4 py-2.5 text-right">Nominal</th>
                  <th className="px-4 py-2.5 text-right">Kode Unik</th>
                  <th className="px-4 py-2.5 text-right">Total Transfer</th>
                  <th className="px-4 py-2.5">Bank</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Diproses</th>
                  {isSuper && <th className="px-4 py-2.5 text-right">Aksi</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((d) => (
                  <tr key={d.id} className="hover:bg-accent/40" data-testid={`deposit-row-${d.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground whitespace-nowrap">{fmtDT(d.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-xs">{d.name || "-"}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">{d.email}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold">{rupiah(d.amount)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground">{d.unique_code != null ? `+${d.unique_code}` : "-"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">{d.total_transfer ? rupiah(d.total_transfer) : "-"}</td>
                    <td className="px-4 py-2.5 text-xs">{d.bank || "-"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${STATUS_BADGE[d.status] || STATUS_BADGE.pending}`} data-testid={`deposit-status-${d.id}`}>
                        {STATUS_LABEL[d.status] || d.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[11px] text-muted-foreground">
                      {d.processed_at ? (<>{fmtDT(d.processed_at)}{d.processed_by ? <div className="font-mono">{d.processed_by}</div> : null}</>) : "-"}
                      {d.approved_after_expired && <div className="text-amber-500 text-[10px]">disetujui setelah expired</div>}
                    </td>
                    {isSuper && (
                      <td className="px-4 py-2.5 text-right">
                        {d.status === "expired" && (
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={approving === d.id} onClick={() => approveExpired(d)} data-testid={`btn-approve-expired-${d.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{approving === d.id ? "Memproses..." : "Setujui"}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={isSuper ? 9 : 8} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="deposit-empty">
                    {status === "approved" ? "Belum ada deposit sukses" : status === "cancel" ? "Tidak ada deposit yang cancel/ditolak" : status === "expired" ? "Tidak ada deposit expired" : "Belum ada riwayat deposit"}
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
